import { useAuthStore } from '../stores/auth'
export interface ApiError {
  error: string
  details?: string
  statusCode?: number
}
export interface ApiResponse {
  statusCode: number
  data: any
}
function shouldNotify(message: any): boolean {
  if (!message) return false
  return !useAuthStore().loggingOut
}
export class ApiErrorService {
  static handleApiResponse(response: ApiResponse, message: any, successMessage?: string): boolean {
    const { statusCode, data } = response
    if (statusCode === 409) {
      this.handleConflictError(data, message)
      return false
    }
    if (statusCode === 403) {
      this.handleForbiddenError(data, message)
      return false
    }
    if (statusCode === 400) {
      this.handleBadRequestError(data, message)
      return false
    }
    if (statusCode === 500) {
      this.handleServerError(data, message)
      return false
    }
    if (statusCode !== 200) {
      this.handleGenericError(statusCode, data, message)
      return false
    }
    if (successMessage && shouldNotify(message)) {
      message.success(successMessage)
    }
    return true
  }
  static handleApiException(error: any, message: any): boolean {
    if (error.response && error.response.statusCode) {
      const statusCode = error.response.statusCode
      const errorData = error.response.data || error.response.body
      if (statusCode === 409) {
        this.handleConflictError(errorData, message)
        return false
      }
      if (statusCode === 403) {
        this.handleForbiddenError(errorData, message)
        return false
      }
      if (statusCode === 400) {
        this.handleBadRequestError(errorData, message)
        return false
      }
      if (statusCode === 500) {
        this.handleServerError(errorData, message)
        return false
      }
    }
    if (shouldNotify(message)) {
      message.error('Failed to complete the operation. Please try again.')
    }
    return false
  }
  private static notifyError(message: any, text: string): void {
    if (shouldNotify(message)) {
      message.error(text)
    }
  }
  private static handleForbiddenError(data: any, message: any): void {
    try {
      const errorData = typeof data === 'string' ? JSON.parse(data) : data
      const errorMessage =
        errorData.error || errorData.description || errorData.message || 'Access denied.'
      this.notifyError(message, errorMessage)
    } catch {
      this.notifyError(message, 'Access denied.')
    }
  }
  private static handleConflictError(data: any, message: any): void {
    try {
      const errorData = typeof data === 'string' ? JSON.parse(data) : data
      const errorMessage =
        errorData.error || errorData.description || errorData.message || 'Resource already exists.'
      this.notifyError(message, errorMessage)
    } catch {
      this.notifyError(message, 'Resource already exists.')
    }
  }
  private static handleBadRequestError(data: any, message: any): void {
    try {
      const errorData = typeof data === 'string' ? JSON.parse(data) : data
      const errorMessage =
        errorData.error ||
        errorData.description ||
        errorData.message ||
        'Invalid request data provided.'
      this.notifyError(message, errorMessage)
    } catch {
      this.notifyError(message, 'Invalid request data provided.')
    }
  }
  private static handleServerError(data: any, message: any): void {
    try {
      const errorData = typeof data === 'string' ? JSON.parse(data) : data
      const errorMessage =
        errorData.error ||
        errorData.description ||
        errorData.message ||
        'Server error occurred. Please try again later.'
      this.notifyError(message, errorMessage)
    } catch {
      this.notifyError(message, 'Server error occurred. Please try again later.')
    }
  }
  private static handleGenericError(statusCode: number, data: any, message: any): void {
    this.notifyError(message, `Request failed with status ${statusCode}. Please try again.`)
  }
}
